import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import StorageIcon from "@mui/icons-material/Storage";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { API_BASE } from "../services/api";

// --- Types ---
interface AgentStep {
  step_name: string;
  step_prompt?: string;
  mcp_module_keys: string[];
  data_bundle_ids?: string[];
}

interface AgentItem {
  id: number;
  name: string;
  description: string;
  steps: AgentStep[];
  org_id: string;
  user_id: string;
  created_at: string;
}

interface MyAccessItem {
  module_key: string;
}

interface DataBundleItem {
  id: string;
  name: string;
}

export default function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();

  // --- States ---
  const [agent, setAgent] = useState<AgentItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit Mode States
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editSteps, setEditSteps] = useState<AgentStep[]>([]);
  const [saving, setSaving] = useState(false);

  // Resources for Editing
  const [myMcps, setMyMcps] = useState<MyAccessItem[]>([]);
  const [myBundles, setMyBundles] = useState<DataBundleItem[]>([]);

  // Step Adder in Edit Mode
  const [stepNameInput, setStepNameInput] = useState("");
  const [stepPromptInput, setStepPromptInput] = useState("");
  const [selectedMcps, setSelectedMcps] = useState<string[]>([]);
  const [selectedBundles, setSelectedBundles] = useState<string[]>([]);

  const myUserId = localStorage.getItem("user_id") || "";

  const authHeaders = useMemo(
    () => ({
      "x-user-id": localStorage.getItem("user_id") || "",
      "x-access-token": localStorage.getItem("access_token") || "",
      "x-refresh-token": localStorage.getItem("refresh_token") || "",
    }),
    []
  );

  // --- Fetchers ---
  const fetchAgentDetail = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE}agents/${agentId}`, { headers: authHeaders });
      if (res.data?.success) {
        setAgent(res.data.data);
      } else {
        setError(res.data?.message || "Agent 정보를 불러오지 못했습니다.");
      }
    } catch (e: any) {
      console.error(e);
      setError(e.response?.data?.message || "Agent 상세 조회 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      // 1. MCP 목록 조회
      const mcpRes = await axios.get(`${API_BASE}mcp/me/access`, {
        headers: authHeaders,
        params: { status: "approved" },
      });
      if (mcpRes.data.success) setMyMcps(mcpRes.data.data || []);

      // 2. Data Bundle 목록 조회
      const bundleRes = await axios.get(`${API_BASE}datalake/bundles`, {
        headers: authHeaders,
      });
      
      if (bundleRes.data && (Array.isArray(bundleRes.data) || bundleRes.data.success)) {
          const list = Array.isArray(bundleRes.data) ? bundleRes.data : (bundleRes.data.data || []);
          
          setMyBundles(list.map((b: any) => ({
              id: b.bundle_id,  
              name: b.name 
          })));
      } else {
          setMyBundles([]);
      }

    } catch (e) {
      console.warn("리소스 로드 실패 (MCP 또는 DataLake 연동 오류)", e);
      setMyBundles([]); 
    }
  };

  useEffect(() => {
    if (agentId) {
      fetchAgentDetail();
      fetchResources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // --- Handlers ---
  const handleEditStart = () => {
    if (!agent) return;
    setEditName(agent.name);
    setEditDesc(agent.description);
    setEditSteps(JSON.parse(JSON.stringify(agent.steps)));
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setStepNameInput("");
    setStepPromptInput("");
    setSelectedMcps([]);
    setSelectedBundles([]);
  };

  const handleStepMcpChange = (event: SelectChangeEvent<typeof selectedMcps>) => {
    const { value } = event.target;
    setSelectedMcps(typeof value === "string" ? value.split(",") : value);
  };

  const handleStepBundleChange = (event: SelectChangeEvent<typeof selectedBundles>) => {
    const { value } = event.target;
    setSelectedBundles(typeof value === "string" ? value.split(",") : value);
  };

  const handleAddStep = () => {
    if (!stepNameInput.trim()) return;
    setEditSteps((prev) => [
      ...prev,
      {
        step_name: stepNameInput,
        step_prompt: stepPromptInput,
        mcp_module_keys: selectedMcps,
        data_bundle_ids: selectedBundles,
      },
    ]);
    setStepNameInput("");
    setStepPromptInput("");
    setSelectedMcps([]);
    setSelectedBundles([]);
  };

  const handleRemoveStep = (idx: number) => {
    setEditSteps((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!agent) return;
    setSaving(true);
    try {
      const payload = {
        name: editName,
        description: editDesc,
        steps: editSteps,
      };
      
      const res = await axios.put(`${API_BASE}agents/${agent.id}`, payload, { headers: authHeaders });
      
      if (res.data?.success) {
        setAgent(res.data.data); 
        setIsEditing(false);
        alert("Agent가 수정되었습니다.");
      } else {
        alert(res.data?.message || "수정에 실패했습니다.");
      }
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.message || "수정 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("정말 이 Agent를 삭제하시겠습니까? 복구할 수 없습니다.")) return;
    try {
      const res = await axios.delete(`${API_BASE}agents/${agentId}`, { headers: authHeaders });
      
      if (res.data?.success) {
        alert("삭제되었습니다.");
        navigate("/"); 
      } else {
        alert(res.data?.message || "삭제에 실패했습니다.");
      }
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.message || "삭제 중 오류가 발생했습니다.");
    }
  };

  // --- Render ---
  const isMine = agent?.user_id === myUserId;

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !agent) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error" variant="h6">{error || "Agent를 찾을 수 없습니다."}</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/")} sx={{ mt: 2 }}>
          목록으로 돌아가기
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      {/* Header Navigation */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate("/")} 
        sx={{ mb: 2, color: "grey.400" }}
      >
        목록으로
      </Button>

      {/* Main Content */}
      <Paper variant="outlined" sx={{ p: 4, bgcolor: "#1e1e1e", color: "#fff", mb: 3 }}>
        {/* Title & Actions */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            {isEditing ? (
              <TextField
                label="Agent Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
                variant="standard"
                InputProps={{ style: { color: "#fff", fontSize: "1.5rem", fontWeight: "bold" } }}
                InputLabelProps={{ style: { color: "#aaa" } }}
                sx={{ mb: 2, minWidth: 400 }}
              />
            ) : (
              <Stack direction="row" alignItems="center" spacing={2}>
                <SmartToyIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {agent.name}
                  </Typography>
                  <Stack direction="row" spacing={1} mt={0.5}>
                    <Chip label={`ID: ${agent.id}`} size="small" variant="outlined" sx={{ color: "#888", borderColor: "#444" }} />
                    <Chip label={`작성자: ${agent.user_id}`} size="small" sx={{ bgcolor: "#333", color: "#ccc" }} />
                    <Chip label={`조직: ${agent.org_id}`} size="small" color="primary" variant="outlined" />
                  </Stack>
                </Box>
              </Stack>
            )}
          </Box>

          {/* Action Buttons */}
          {isMine && !isEditing && (
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEditStart}>수정</Button>
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>삭제</Button>
            </Stack>
          )}
          {isEditing && (
            <Stack direction="row" spacing={1}>
              <Button variant="text" color="inherit" onClick={handleEditCancel}>취소</Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? "저장 중..." : "저장"}
              </Button>
            </Stack>
          )}
        </Box>

        <Divider sx={{ my: 3, borderColor: "#444" }} />

        {/* Description */}
        <Box mb={4}>
          <Typography variant="h6" gutterBottom color="primary.light">설명</Typography>
          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              InputProps={{ style: { color: "#fff" }, sx: { ".MuiOutlinedInput-notchedOutline": { borderColor: "#555" } } }}
            />
          ) : (
            <Typography variant="body1" color="grey.300" sx={{ lineHeight: 1.8 }}>
              {agent.description}
            </Typography>
          )}
        </Box>

        {/* Workflow Steps (Timeline Style) */}
        <Typography variant="h6" gutterBottom color="primary.light" mb={3}>
          실행 단계 (Workflow)
        </Typography>

        {/* View Mode (Timeline) */}
        {!isEditing && (
          <Box sx={{ position: "relative", ml: 1 }}>
            {/* Vertical Line */}
            <Box
              sx={{
                position: "absolute",
                top: 10,
                bottom: 20, 
                left: 9,
                width: 2,
                bgcolor: "#444",
                zIndex: 0,
              }}
            />

            <Stack spacing={3}>
              {agent.steps.map((step, idx) => (
                <Box key={idx} sx={{ position: "relative", pl: 5 }}>
                  {/* Timeline Node (Circle) */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 16,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      bgcolor: "#1e1e1e",
                      border: "4px solid #1976d2",
                      zIndex: 1,
                    }}
                  />

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      bgcolor: "#252525",
                      borderColor: "#444",
                      transition: "transform 0.2s",
                      "&:hover": { borderColor: "#666" },
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: "#fff" }}>
                      {idx + 1}. {step.step_name}
                    </Typography>

                    {step.step_prompt && (
                        <Box sx={{ mt: 1, mb: 2, p: 1.5, bgcolor: "rgba(255,255,0,0.05)", borderRadius: 1, borderLeft: "3px solid #fbc02d" }}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                <LightbulbIcon sx={{ fontSize: 16, color: "#fbc02d" }} />
                                <Typography variant="caption" color="#fbc02d" fontWeight="bold">Prompt</Typography>
                            </Box>
                            <Typography variant="body2" color="grey.300" sx={{ whiteSpace: "pre-wrap" }}>
                                {step.step_prompt}
                            </Typography>
                        </Box>
                    )}

                    <Divider sx={{ my: 1.5, borderColor: "#444" }} />

                    {/* Tools Section */}
                    <Box display="flex" alignItems="flex-start" gap={2} mb={1.5}>
                      <Typography variant="body2" color="grey.500" sx={{ minWidth: 50, pt: 0.5 }}>
                        Tools:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {step.mcp_module_keys.length > 0 ? (
                          step.mcp_module_keys.map((k) => (
                            <Chip
                              key={k}
                              label={k}
                              size="small"
                              icon={<SettingsSuggestIcon style={{ fontSize: 14 }} />}
                              sx={{ bgcolor: "#333", color: "#ddd", border: "1px solid #555" }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="grey.600" sx={{ pt: 0.5 }}>없음</Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Data Section */}
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Typography variant="body2" color="grey.500" sx={{ minWidth: 50, pt: 0.5 }}>
                        Data:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {step.data_bundle_ids && step.data_bundle_ids.length > 0 ? (
                          step.data_bundle_ids.map((b) => {
                            // ✅ [수정] ID로 이름 찾기 (View Mode)
                            const bundleName = myBundles.find((mb) => mb.id === b)?.name || b;
                            return (
                              <Chip
                                key={b}
                                label={bundleName} // <-- 이름 표시
                                size="small"
                                variant="outlined"
                                color="secondary"
                                icon={<StorageIcon style={{ fontSize: 14 }} />}
                                sx={{ border: "1px solid" }}
                              />
                            );
                          })
                        ) : (
                          <Typography variant="body2" color="grey.600" sx={{ pt: 0.5 }}>없음</Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Edit Mode (Form) */}
        {isEditing && (
          <Box>
            <Stack spacing={2} mb={3}>
              {editSteps.map((step, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 2, bgcolor: "#252525" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography fontWeight="bold">Step {idx + 1}: {step.step_name}</Typography>
                        {step.step_prompt && (
                            <Typography variant="body2" color="grey.500" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                💡 {step.step_prompt.substring(0, 50)}{step.step_prompt.length > 50 ? "..." : ""}
                            </Typography>
                        )}
                        <Typography variant="caption" color="grey.400" display="block" mt={1}>
                          MCP: {step.mcp_module_keys.join(", ") || "-"} | 
                          {/* ✅ [수정] ID로 이름 찾기 (Edit Mode List) */}
                          Data: {
                            step.data_bundle_ids && step.data_bundle_ids.length > 0
                              ? step.data_bundle_ids.map(bid => myBundles.find(b => b.id === bid)?.name || bid).join(", ")
                              : "-"
                          }
                        </Typography>
                      </Box>
                      <IconButton color="error" onClick={() => handleRemoveStep(idx)}>
                        <DeleteIcon />
                      </IconButton>
                  </Box>
                </Paper>
              ))}
            </Stack>

            {/* Add Step Form */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "rgba(255,255,255,0.05)", borderColor: "#555" }}>
              <Typography variant="subtitle2" gutterBottom>새 단계 추가</Typography>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="단계 이름"
                    fullWidth
                    size="small"
                    value={stepNameInput}
                    onChange={(e) => setStepNameInput(e.target.value)}
                    InputProps={{ style: { color: "#fff" }, sx: { ".MuiOutlinedInput-notchedOutline": { borderColor: "#666" } } }}
                    InputLabelProps={{ style: { color: "#aaa" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={9}>
                    <TextField
                        label="프롬프트 (선택 사항)"
                        fullWidth
                        size="small"
                        value={stepPromptInput}
                        onChange={(e) => setStepPromptInput(e.target.value)}
                        placeholder="이 단계에서 AI가 수행할 구체적인 지시사항을 입력하세요."
                        InputProps={{ style: { color: "#fff" }, sx: { ".MuiOutlinedInput-notchedOutline": { borderColor: "#666" } } }}
                        InputLabelProps={{ style: { color: "#aaa" } }}
                    />
                </Grid>
                
                <Grid item xs={12} sm={5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ color: "#aaa" }}>MCP 선택</InputLabel>
                    <Select
                      multiple
                      value={selectedMcps}
                      onChange={handleStepMcpChange}
                      input={<OutlinedInput label="MCP 선택" sx={{ color: "#fff", ".MuiOutlinedInput-notchedOutline": { borderColor: "#666" } }} />}
                      MenuProps={{ PaperProps: { sx: { bgcolor: "#333", color: "#fff" } } }}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => <Chip key={value} label={value} size="small" sx={{ bgcolor: "#555", color: "#fff" }} />)}
                        </Box>
                      )}
                    >
                      {myMcps.map((m) => <MenuItem key={m.module_key} value={m.module_key}>{m.module_key}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* ✅ [수정 2] Data Bundle 선택 부분: renderValue 로직 개선 (이름 표시) */}
                <Grid item xs={12} sm={5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ color: "#aaa" }}>Data 선택</InputLabel>
                    <Select
                      multiple
                      value={selectedBundles}
                      onChange={handleStepBundleChange}
                      input={<OutlinedInput label="Data 선택" sx={{ color: "#fff", ".MuiOutlinedInput-notchedOutline": { borderColor: "#666" } }} />}
                      MenuProps={{ PaperProps: { sx: { bgcolor: "#333", color: "#fff" } } }}
                      
                      // RenderValue 로직 수정: ID 대신 Name 표시
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => {
                            const bundle = myBundles.find((b) => b.id === value);
                            return (
                              <Chip 
                                key={value} 
                                label={bundle ? bundle.name : value} 
                                size="small" 
                                color="secondary" 
                                variant="outlined" 
                                sx={{ color: "#ff8a80", borderColor: "#ff8a80" }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {myBundles.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={2}>
                  <Button variant="contained" fullWidth onClick={handleAddStep} disabled={!stepNameInput} sx={{ height: 40 }}>추가</Button>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
}